import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
  Snackbar,
  IconButton,
  Stack,
  LinearProgress,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid } from "@mui/x-data-grid";
import ModalDialogTitle from "../../common/ModalDialogTitle";
import ModalActions from "../../common/ModalActions";
import ConfirmDeleteDialog from "../../common/ConfirmDeleteDialog";
import * as XLSX from "xlsx";
import { db } from "../../../config/firebase";
import {
  collection,
  getDocs,
  addDoc,
  setDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import CcpCpBreadcrumbs from "../../Layout/inc/CcpCpBreadcrumbs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileImport,
  faFileDownload,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";

const initialForm = {
  TermName: "",
  TermShortname: "",
  StartDate: "",
  EndDate: "",
};

const CcpAdminTerm = () => {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState({ TermName: "", TermShortname: "" });
  const [filteredData, setFilteredData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [importStatus, setImportStatus] = useState({
    open: false,
    added: 0,
    updated: 0,
    total: 0,
    loading: false,
  });
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const COLLECTION_NAME = "CcpTerm";

  useEffect(() => {
    const fetchData = async () => {
      const snapshot = await getDocs(collection(db, COLLECTION_NAME));
      let rows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows = rows.sort((a, b) =>
        (a.TermName || "").localeCompare(b.TermName || ""),
      );
      setData(rows);
    };
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = data.filter(
      (row) =>
        (!filter.TermName ||
          (row.TermName || "")
            .toLowerCase()
            .includes(filter.TermName.toLowerCase())) &&
        (!filter.TermShortname ||
          (row.TermShortname || "")
            .toLowerCase()
            .includes(filter.TermShortname.toLowerCase())),
    );
    filtered = filtered.sort((a, b) =>
      (a.TermName || "").localeCompare(b.TermName || ""),
    );
    setFilteredData(filtered);
  }, [data, filter]);

  const handleOpenAdd = () => {
    setEditIndex(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const handleOpenEdit = (params) => {
    const idx = data.findIndex((d) => d.id === params.row.id);
    setEditIndex(idx);
    // Normalize date values so the date inputs (type=date) show correctly (YYYY-MM-DD)
    const toIsoDate = (v) => {
      if (!v && v !== 0) return "";
      // Firestore Timestamp -> convert to JS Date
      if (v && typeof v.toDate === "function")
        return dayjs(v.toDate()).format("YYYY-MM-DD");
      if (typeof v === "string") {
        if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v; // already ISO
        if (/^\d{2}-\d{2}-\d{4}$/.test(v)) {
          const [m, d, y] = v.split("-");
          return `${y}-${m}-${d}`;
        }
        const parsed = dayjs(v);
        return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
      }
      if (v instanceof Date || typeof v === "number") {
        return dayjs(v).format("YYYY-MM-DD");
      }
      return "";
    };

    setForm({
      ...params.row,
      StartDate: toIsoDate(params.row?.StartDate),
      EndDate: toIsoDate(params.row?.EndDate),
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleDateChange = (name, value) => {
    setForm({ ...form, [name]: value });
  };

  const handleSave = async () => {
    if (!form.TermName || !form.TermShortname) {
      setSnackbar({
        open: true,
        message: "All fields are required.",
        severity: "error",
      });
      return;
    }
    try {
      if (editIndex !== null) {
        const docId = data[editIndex].id;
        await setDoc(doc(db, COLLECTION_NAME, docId), form);
        const newData = [...data];
        newData[editIndex] = { ...form, id: docId };
        setData(newData);
        setSnackbar({
          open: true,
          message: "Term updated",
          severity: "success",
        });
      } else {
        const docRef = await addDoc(collection(db, COLLECTION_NAME), form);
        setData([...data, { ...form, id: docRef.id }]);
        setSnackbar({ open: true, message: "Term added", severity: "success" });
      }
      setModalOpen(false);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error saving to Firestore",
        severity: "error",
      });
    }
  };

  const handleDeleteTerm = async () => {
    if (editIndex !== null) {
      const docId = data[editIndex].id;
      await deleteDoc(doc(db, COLLECTION_NAME, docId));
      const newData = data.filter((_, idx) => idx !== editIndex);
      setData(newData);
      setModalOpen(false);
      setSnackbar({ open: true, message: "Term deleted", severity: "success" });
    }
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportStatus({
      open: true,
      added: 0,
      updated: 0,
      total: 0,
      loading: true,
    });
    const reader = new FileReader();

    const processExcelRow = async (row, merged, counts) => {
      const { added, updated } = counts;
      const idx = merged.findIndex(
        (d) =>
          d.TermName === row.TermName && d.TermShortname === row.TermShortname,
      );
      if (idx > -1) {
        const docId = merged[idx].id;
        await setDoc(doc(db, COLLECTION_NAME, docId), row);
        merged[idx] = { ...merged[idx], ...row };
        counts.updated = updated + 1;
      } else {
        const docRef = await addDoc(collection(db, COLLECTION_NAME), row);
        merged.push({ ...row, id: docRef.id });
        counts.added = added + 1;
      }
    };

    reader.onload = async (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const jsonData = XLSX.utils.sheet_to_json(ws);

      const merged = [...data];
      const counts = { added: 0, updated: 0 };

      for (const row of jsonData) {
        await processExcelRow(row, merged, counts);
        setImportStatus((s) => ({
          ...s,
          total: jsonData.length,
          added: counts.added,
          updated: counts.updated,
        }));
      }

      setData(merged);
      setSnackbar({
        open: true,
        message: "Excel data imported",
        severity: "success",
      });
      setImportStatus((s) => ({ ...s, loading: false }));
    };

    reader.readAsBinaryString(file);
  };

  const handleDeleteMultipleConfirmed = async () => {
    for (const id of rowSelectionModel) {
      await deleteDoc(doc(db, COLLECTION_NAME, id));
    }
    setData(data.filter((row) => !rowSelectionModel.includes(row.id)));
    setRowSelectionModel([]);
    setConfirmDeleteOpen(false);
    setSnackbar({
      open: true,
      message: "Selected terms deleted",
      severity: "success",
    });
  };

  const handleOpenConfirmDelete = () => {
    if (!rowSelectionModel || rowSelectionModel.length === 0) return;
    setConfirmDeleteOpen(true);
  };

  const handleDownloadTemplate = () => {
    const headers = ["TermName", "TermShortname", "StartDate", "EndDate"];
    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
    XLSX.writeFile(workbook, "ccp-term-template.xlsx");
  };

  const formatDateForGrid = (v) => {
    if (v === undefined || v === null || v === "") return "";
    // Firestore Timestamp
    if (v && typeof v.toDate === "function")
      return dayjs(v.toDate()).format("MM-DD-YYYY");
    if (typeof v === "string") {
      if (/^\d{2}-\d{2}-\d{4}$/.test(v)) return v;
      if (/^\d{4}-\d{2}-\d{2}/.test(v)) return dayjs(v).format("MM-DD-YYYY");
      const parsed = dayjs(v);
      return parsed.isValid() ? parsed.format("MM-DD-YYYY") : v;
    }
    if (v instanceof Date || typeof v === "number") {
      const parsed = dayjs(v);
      return parsed.isValid() ? parsed.format("MM-DD-YYYY") : "";
    }
    return String(v);
  };

  const rowsForGrid = filteredData.map((row) => ({
    ...row,
    id: String(row.id),
    StartDateDisplay: formatDateForGrid(row.StartDate),
    EndDateDisplay: formatDateForGrid(row.EndDate),
  }));

  const columns = [
    { field: "TermName", headerName: "Term Name", flex: 1, sortable: true },
    {
      field: "TermShortname",
      headerName: "Term Shortname",
      flex: 1,
      sortable: true,
    },
    {
      field: "StartDateDisplay",
      headerName: "Start Date",
      flex: 1,
      sortable: true,
    },
    {
      field: "EndDateDisplay",
      headerName: "End Date",
      flex: 1,
      sortable: true,
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 1 }}>
        CCP Term Management
      </Typography>
      <CcpCpBreadcrumbs pageName="Sinclair Terms" />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        mb={2}
        alignItems="center"
        sx={{ mt: 3, mb: 3 }}
      >
        <TextField
          label="Filter Term Name"
          name="TermName"
          value={filter.TermName}
          onChange={(e) => setFilter({ ...filter, TermName: e.target.value })}
          size="small"
        />
        <TextField
          label="Filter Term Shortname"
          name="TermShortname"
          value={filter.TermShortname}
          onChange={(e) =>
            setFilter({ ...filter, TermShortname: e.target.value })
          }
          size="small"
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
        >
          Add
        </Button>
        <IconButton component="label" size="medium">
          <FontAwesomeIcon icon={faFileImport} color="#2e7d32" />
          <input
            type="file"
            accept=".xlsx, .xls"
            hidden
            onChange={handleExcelUpload}
          />
        </IconButton>
        <IconButton onClick={handleDownloadTemplate} size="medium">
          <FontAwesomeIcon icon={faFileDownload} color="#1976d2" />
        </IconButton>
        {rowSelectionModel && rowSelectionModel.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleOpenConfirmDelete}
            sx={{ ml: 1 }}
          >
            Delete Selected
          </Button>
        )}
      </Stack>
      <Box
        sx={{
          height: 500,
          width: "100%",
          background: "#fff",
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <DataGrid
          rows={rowsForGrid}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          disableSelectionOnClick
          onRowDoubleClick={handleOpenEdit}
          checkboxSelection
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newSelectionModel) => {
            setRowSelectionModel(newSelectionModel);
          }}
        />
      </Box>
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="xs"
        fullWidth
      >
        <ModalDialogTitle onClose={handleCloseModal}>
          {editIndex !== null ? "Edit Term" : "Add Term"}
        </ModalDialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Term Name"
              name="TermName"
              value={form.TermName}
              onChange={handleFormChange}
              required
              fullWidth
            />
            <TextField
              label="Term Shortname"
              name="TermShortname"
              value={form.TermShortname}
              onChange={handleFormChange}
              required
              fullWidth
            />
            <TextField
              label="Start Date"
              type="date"
              name="StartDate"
              value={form.StartDate}
              onChange={handleFormChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              name="EndDate"
              value={form.EndDate}
              onChange={handleFormChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <ModalActions
          onSave={handleSave}
          onDelete={editIndex !== null ? handleDeleteTerm : undefined}
        />
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteMultipleConfirmed}
        title="Delete Selected Terms"
        message={`Are you sure you want to delete ${rowSelectionModel.length} selected term${
          rowSelectionModel.length === 1 ? "" : "s"
        }? This action cannot be undone.`}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
      {importStatus.open && (
        <Box sx={{ mt: 2 }}>
          {importStatus.loading && <LinearProgress />}
          <Alert severity="info" sx={{ mt: 1 }}>
            Importing Excel:{" "}
            {importStatus.loading ? "Processing..." : "Complete."} <br />
            Added: {importStatus.added} &nbsp; Updated: {importStatus.updated}{" "}
            &nbsp; Total: {importStatus.total}
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default CcpAdminTerm;
